import {
  readDispatchDateFromSearch,
  readLastDispatchContext,
  readDispatchPeriodFromSearch,
} from "../utils/dispatchDateUrls.js";
import {
  fetchDispatchAlertCapturePolicy,
  uploadDispatchAlertCapture,
} from "./dispatchAlertCaptureApi.js";
import {
  parseDispatchAlertCaptureContext,
  resolveDispatchAlertCaptureOperationKey,
  serializeDispatchAlertCaptureContext,
} from "./dispatchAlertCaptureContext.js";

export const DISPATCH_ALERT_CAPTURE_SELECTOR = "[data-dispatch-alert-capture-type]";
export const DISPATCH_ALERT_CAPTURE_POLL_INTERVAL_MS = 15_000;
export const DISPATCH_ALERT_CAPTURE_MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
export const DISPATCH_ALERT_CAPTURE_STABILITY_DELAY_MS = 800;

const RESULTS_PATHNAMES = new Set(["/results", "/results/latest"]);
const SUPPORTED_ALERT_TYPES = new Set([
  "dispatch_failure",
  "global_error",
  "non_compliance",
]);
const CORRELATED_INCIDENT_ALERT_TYPES = new Set([
  "dispatch_failure",
  "global_error",
]);
const POLICY_INVALIDATING_UPLOAD_STATUSES = new Set([401, 403, 409, 410]);
const CAMPAIGN_FULL_ERROR_CODE = "alert_capture_campaign_full";
const CAPTURE_FAILURE_RETRY_DELAY_MS = 30_000;
const INCIDENT_CORRELATION_WINDOW_MS = 5_000;
const POLICY_REQUEST_TIMEOUT_MS = 10_000;
const MAX_ALERT_TEXT_LENGTH = 4_000;

function resolveWindow(windowObject) {
  if (windowObject && typeof windowObject === "object") {
    return windowObject;
  }
  return typeof window === "object" ? window : null;
}

function resolveDocument(documentObject) {
  if (documentObject && typeof documentObject === "object") {
    return documentObject;
  }
  return typeof document === "object" ? document : null;
}

function normalizeText(value, maxLength = MAX_ALERT_TEXT_LENGTH) {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function normalizePositiveInteger(value, fallbackValue) {
  const normalizedValue = Number(value);
  return Number.isFinite(normalizedValue) && normalizedValue > 0
    ? Math.round(normalizedValue)
    : fallbackValue;
}

function parseTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

export function isDispatchAlertCapturePath(pathname) {
  return RESULTS_PATHNAMES.has(typeof pathname === "string" ? pathname : "");
}

export function normalizeDispatchAlertCapturePolicy(payload) {
  const allowedAlertTypes = Array.isArray(payload?.allowed_alert_types)
    ? [...new Set(payload.allowed_alert_types.filter((item) => SUPPORTED_ALERT_TYPES.has(item)))]
    : [];
  const campaignId = normalizeText(payload?.campaign_id, 128);
  const expiresAt = normalizeText(payload?.expires_at, 128);

  return {
    allowedAlertTypes,
    campaignId,
    enabled: payload?.enabled === true
      && payload?.is_active === true
      && payload?.is_targeted === true
      && Boolean(campaignId),
    expiresAt,
    maxUploadBytes: normalizePositiveInteger(
      payload?.max_upload_bytes,
      DISPATCH_ALERT_CAPTURE_MAX_UPLOAD_BYTES,
    ),
    pollIntervalMs: normalizePositiveInteger(
      Number(payload?.poll_interval_seconds) * 1000,
      DISPATCH_ALERT_CAPTURE_POLL_INTERVAL_MS,
    ),
    revision: Number.isInteger(payload?.revision) ? payload.revision : 0,
  };
}

export function createDispatchAlertCaptureId(randomUuid = globalThis.crypto?.randomUUID?.bind(globalThis.crypto)) {
  if (typeof randomUuid === "function") {
    return randomUuid();
  }

  const bytes = new Uint8Array(16);
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function renderDispatchAlertNodeToPng(node, {
  pixelRatio = Math.min(Math.max(globalThis.devicePixelRatio || 1, 1), 2),
} = {}) {
  const { toBlob } = await import("html-to-image");
  const image = await toBlob(node, {
    backgroundColor: "#ffffff",
    cacheBust: true,
    filter: (candidateNode) => candidateNode?.getAttribute?.("data-dispatch-alert-capture-ignore") !== "true",
    pixelRatio,
  });

  if (!image) {
    throw new Error("La bannière n'a pas pu être convertie en PNG.");
  }
  return image;
}

function isPolicyCurrentlyEnabled(policy, nowMs) {
  if (!policy?.enabled || !policy.campaignId || policy.allowedAlertTypes.length === 0) {
    return false;
  }
  const expiresAt = parseTimestamp(policy.expiresAt);
  return expiresAt == null || expiresAt > nowMs;
}

function isVisibleCaptureNode(node) {
  if (!node || node.isConnected === false || typeof node.getBoundingClientRect !== "function") {
    return false;
  }
  const bounds = node.getBoundingClientRect();
  return Number(bounds?.width) > 0 && Number(bounds?.height) > 0;
}

function readCaptureNodeText(node) {
  const title = normalizeText(
    node?.getAttribute?.("data-dispatch-alert-capture-title"),
  );
  const message = normalizeText(
    node?.getAttribute?.("data-dispatch-alert-capture-message"),
  );
  return {
    alertText: title || message
      ? normalizeText([title, message].filter(Boolean).join(" — "))
      : normalizeText(node?.textContent),
    incidentText: message
      ? normalizeText([title, message].filter(Boolean).join("\u001e")).toLocaleLowerCase("fr-CA")
      : "",
    surfaceText: normalizeText(node?.textContent),
  };
}

function createCaptureFingerprint({
  alertText,
  alertType,
  campaignId,
  captureContext,
  dispatchDate,
  dispatchPeriod,
  node,
  sourcePath,
  surfaceText,
}) {
  const occurrence = normalizeText(
    node?.getAttribute?.("data-dispatch-alert-capture-occurrence"),
    512,
  );
  return [
    campaignId,
    alertType,
    occurrence,
    sourcePath,
    dispatchDate,
    dispatchPeriod,
    alertText,
    surfaceText,
    serializeDispatchAlertCaptureContext(captureContext),
  ].join("\u001f");
}

function createCorrelatedIncidentKey({
  alertType,
  campaignId,
  captureContext,
  dispatchDate,
  dispatchPeriod,
  incidentText,
  sourcePath,
}) {
  if (
    !CORRELATED_INCIDENT_ALERT_TYPES.has(alertType)
    || !incidentText
  ) {
    return "";
  }
  return [
    campaignId,
    sourcePath,
    dispatchDate,
    dispatchPeriod,
    resolveDispatchAlertCaptureOperationKey(captureContext),
    incidentText,
  ].join("\u001f");
}

function readRouteContext(route) {
  const pathname = typeof route?.pathname === "string" ? route.pathname : "";
  const search = typeof route?.search === "string" ? route.search : "";
  const storedContext = readLastDispatchContext();
  const searchParams = new URLSearchParams(search);
  const explicitDispatchDate = readDispatchDateFromSearch(search);
  return {
    dispatchDate: explicitDispatchDate || storedContext.dispatch_date || "",
    dispatchPeriod: searchParams.has("dispatch_period")
      ? readDispatchPeriodFromSearch(search)
      : explicitDispatchDate
        ? readDispatchPeriodFromSearch(search)
        : storedContext.dispatch_period || readDispatchPeriodFromSearch(search),
    pathname,
    search,
  };
}

function createInitialState() {
  return {
    campaignId: "",
    errorMessage: "",
    isActive: false,
    revision: 0,
    status: "idle",
  };
}

export function createDispatchAlertCaptureController({
  AbortControllerImpl = globalThis.AbortController,
  captureStabilityDelayMs = DISPATCH_ALERT_CAPTURE_STABILITY_DELAY_MS,
  MutationObserverImpl = globalThis.MutationObserver,
  clearTimeoutImpl = globalThis.clearTimeout?.bind(globalThis),
  documentObject: providedDocument = null,
  fetchPolicy = fetchDispatchAlertCapturePolicy,
  incidentCorrelationWindowMs = INCIDENT_CORRELATION_WINDOW_MS,
  minimumPollIntervalMs = 5_000,
  now = () => Date.now(),
  onStateChange = () => {},
  policyRequestTimeoutMs = POLICY_REQUEST_TIMEOUT_MS,
  randomUuid = globalThis.crypto?.randomUUID?.bind(globalThis.crypto),
  renderNode = renderDispatchAlertNodeToPng,
  route = null,
  setTimeoutImpl = globalThis.setTimeout?.bind(globalThis),
  uploadCapture = uploadDispatchAlertCapture,
  windowObject: providedWindow = null,
} = {}) {
  const windowObject = resolveWindow(providedWindow);
  const documentObject = resolveDocument(providedDocument) || windowObject?.document || null;
  const capturedFingerprintByNode = new WeakMap();
  const capturedOccurrenceFingerprints = new Set();
  const captureAttemptByNode = new WeakMap();
  const captureAttemptByFingerprint = new Map();
  const captureFailureByNode = new WeakMap();
  const capturedIncidentByKey = new Map();
  const capturedIncidentKeyByAlternativeNode = new WeakMap();
  const captureStabilityByNode = new WeakMap();
  const pendingNodes = new WeakSet();
  const activeUploadAbortControllers = new Set();
  let activePollPromise = null;
  let blockedCampaignId = "";
  let blockedCampaignMessage = "";
  let cachedPolicy = null;
  let currentEtag = "";
  let currentRoute = readRouteContext(route || windowObject?.location || {});
  let disposed = false;
  let mutationObserver = null;
  let policyAbortController = null;
  let policyFreshnessTimeoutId = null;
  let policyRequestTimeoutId = null;
  let policyVerifiedAt = null;
  let pollTimeoutId = null;
  let scanDueAt = null;
  let scanTimeoutId = null;
  let started = false;
  let state = createInitialState();

  function publishState(nextState) {
    const normalizedState = {
      campaignId: nextState.campaignId || "",
      errorMessage: nextState.errorMessage || "",
      isActive: nextState.isActive === true,
      revision: Number.isInteger(nextState.revision) ? nextState.revision : 0,
      status: nextState.status || "idle",
    };
    if (
      normalizedState.campaignId === state.campaignId
      && normalizedState.errorMessage === state.errorMessage
      && normalizedState.isActive === state.isActive
      && normalizedState.revision === state.revision
      && normalizedState.status === state.status
    ) {
      return;
    }
    state = normalizedState;
    onStateChange({ ...state });
  }

  function isDocumentVisible() {
    return !documentObject || documentObject.visibilityState !== "hidden";
  }

  function canRunOnCurrentRoute() {
    return isDispatchAlertCapturePath(currentRoute.pathname);
  }

  function clearPollTimer() {
    if (pollTimeoutId != null && typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(pollTimeoutId);
    }
    pollTimeoutId = null;
  }

  function clearPolicyFreshnessTimer() {
    if (policyFreshnessTimeoutId != null && typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(policyFreshnessTimeoutId);
    }
    policyFreshnessTimeoutId = null;
  }

  function clearScanTimer() {
    if (scanTimeoutId != null && typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(scanTimeoutId);
    }
    scanDueAt = null;
    scanTimeoutId = null;
  }

  function disconnectObserver() {
    mutationObserver?.disconnect?.();
    mutationObserver = null;
    clearScanTimer();
  }

  function stopCaptureActivity(status = "paused", errorMessage = "") {
    disconnectObserver();
    clearPolicyFreshnessTimer();
    for (const controller of activeUploadAbortControllers) {
      controller.abort?.();
    }
    activeUploadAbortControllers.clear();
    publishState({
      campaignId: cachedPolicy?.campaignId || "",
      errorMessage,
      isActive: false,
      revision: cachedPolicy?.revision || 0,
      status,
    });
  }

  function waitForPaint() {
    return new Promise((resolve) => {
      if (typeof windowObject?.requestAnimationFrame === "function") {
        windowObject.requestAnimationFrame(() => {
          windowObject.requestAnimationFrame(resolve);
        });
        return;
      }
      if (typeof setTimeoutImpl === "function") {
        setTimeoutImpl(resolve, 0);
        return;
      }
      resolve();
    });
  }

  function resolvePolicyFreshnessWindow(policy) {
    const pollInterval = Math.max(
      policy?.pollIntervalMs || DISPATCH_ALERT_CAPTURE_POLL_INTERVAL_MS,
      minimumPollIntervalMs,
    );
    const requestGrace = Number.isFinite(policyRequestTimeoutMs) && policyRequestTimeoutMs > 0
      ? policyRequestTimeoutMs
      : 0;
    return pollInterval + requestGrace;
  }

  function isPolicyFresh(policy, nowMs) {
    return Number.isFinite(policyVerifiedAt)
      && nowMs < policyVerifiedAt + resolvePolicyFreshnessWindow(policy);
  }

  function isCaptureStillAllowed(policy, campaignId) {
    return !disposed
      && isDocumentVisible()
      && canRunOnCurrentRoute()
      && cachedPolicy === policy
      && policy.campaignId === campaignId
      && blockedCampaignId !== campaignId
      && isPolicyFresh(policy, now())
      && isPolicyCurrentlyEnabled(policy, now());
  }

  function readCaptureSnapshot(node, policy, routeAtOccurrence = currentRoute) {
    if (!isVisibleCaptureNode(node)) {
      return null;
    }
    const alertType = normalizeText(
      node.getAttribute?.("data-dispatch-alert-capture-type"),
      64,
    );
    if (
      !SUPPORTED_ALERT_TYPES.has(alertType)
      || !policy.allowedAlertTypes.includes(alertType)
    ) {
      return null;
    }

    const {
      alertText,
      incidentText,
      surfaceText,
    } = readCaptureNodeText(node);
    const captureContext = parseDispatchAlertCaptureContext(
      node.getAttribute?.("data-dispatch-alert-capture-context"),
    );
    const fingerprint = createCaptureFingerprint({
      alertText,
      alertType,
      campaignId: policy.campaignId,
      captureContext,
      dispatchDate: routeAtOccurrence.dispatchDate,
      dispatchPeriod: routeAtOccurrence.dispatchPeriod,
      node,
      sourcePath: routeAtOccurrence.pathname,
      surfaceText,
    });
    const occurrence = normalizeText(
      node.getAttribute?.("data-dispatch-alert-capture-occurrence"),
      512,
    );
    const bounds = node.getBoundingClientRect();
    return {
      alertText,
      alertType,
      area: Number(bounds?.width) * Number(bounds?.height),
      captureContext,
      fingerprint,
      hasStableOccurrence: Boolean(occurrence),
      incidentKey: createCorrelatedIncidentKey({
        alertType,
        campaignId: policy.campaignId,
        captureContext,
        dispatchDate: routeAtOccurrence.dispatchDate,
        dispatchPeriod: routeAtOccurrence.dispatchPeriod,
        incidentText,
        sourcePath: routeAtOccurrence.pathname,
      }),
      node,
      route: { ...routeAtOccurrence },
    };
  }

  function isCapturedAlternative(snapshot) {
    if (!snapshot.incidentKey) {
      return false;
    }
    if (capturedIncidentKeyByAlternativeNode.get(snapshot.node) === snapshot.incidentKey) {
      return true;
    }

    const capturedIncident = capturedIncidentByKey.get(snapshot.incidentKey);
    if (!capturedIncident) {
      return false;
    }
    const normalizedWindow = Math.max(0, Number(incidentCorrelationWindowMs) || 0);
    if (capturedIncident.capturedAt + normalizedWindow < now()) {
      capturedIncidentByKey.delete(snapshot.incidentKey);
      return false;
    }
    if (capturedIncident.alertType === snapshot.alertType) {
      return false;
    }

    capturedIncidentKeyByAlternativeNode.set(snapshot.node, snapshot.incidentKey);
    return true;
  }

  function selectCaptureCandidates(nodes, policy) {
    const snapshots = nodes
      .map((node) => readCaptureSnapshot(node, policy))
      .filter(Boolean)
      .filter((snapshot) => !isCapturedAlternative(snapshot));
    const candidates = [];
    const correlatedGroups = new Map();

    for (const snapshot of snapshots) {
      if (!snapshot.incidentKey) {
        candidates.push({
          equivalentSnapshots: [],
          snapshot,
        });
        continue;
      }
      const group = correlatedGroups.get(snapshot.incidentKey) || [];
      group.push(snapshot);
      correlatedGroups.set(snapshot.incidentKey, group);
    }

    for (const group of correlatedGroups.values()) {
      const globalErrors = group.filter(({ alertType }) => alertType === "global_error");
      const dispatchFailures = group.filter(({ alertType }) => alertType === "dispatch_failure");
      if (!globalErrors.length || !dispatchFailures.length) {
        for (const snapshot of group) {
          candidates.push({
            equivalentSnapshots: [],
            snapshot,
          });
        }
        continue;
      }

      const smallestSnapshot = group.reduce((smallest, snapshot) => (
        snapshot.area < smallest.area ? snapshot : smallest
      ));
      const preferredAlertType = globalErrors.length
        ? "global_error"
        : smallestSnapshot.alertType;
      const preferredSnapshots = group.filter(
        ({ alertType }) => alertType === preferredAlertType,
      );
      const equivalentSnapshots = group.filter(
        ({ alertType }) => alertType !== preferredAlertType,
      );
      for (const snapshot of preferredSnapshots) {
        candidates.push({
          equivalentSnapshots,
          snapshot,
        });
      }
    }
    return candidates;
  }

  function rememberCapturedAlternative(snapshot, policy) {
    if (!snapshot?.incidentKey) {
      return;
    }
    capturedIncidentKeyByAlternativeNode.set(snapshot.node, snapshot.incidentKey);
    const currentSnapshot = readCaptureSnapshot(
      snapshot.node,
      policy,
      snapshot.route,
    );
    if (!currentSnapshot || currentSnapshot.incidentKey !== snapshot.incidentKey) {
      return;
    }
    capturedFingerprintByNode.set(snapshot.node, currentSnapshot.fingerprint);
    if (currentSnapshot.hasStableOccurrence) {
      capturedOccurrenceFingerprints.add(currentSnapshot.fingerprint);
    }
  }

  async function captureNode({ equivalentSnapshots = [], snapshot }, policy) {
    const {
      alertText,
      alertType,
      captureContext,
      fingerprint,
      hasStableOccurrence,
      node,
      route: routeAtOccurrence,
    } = snapshot;
    if (!isVisibleCaptureNode(node) || pendingNodes.has(node)) {
      return null;
    }

    if (
      capturedFingerprintByNode.get(node) === fingerprint
      || (hasStableOccurrence && capturedOccurrenceFingerprints.has(fingerprint))
    ) {
      return null;
    }

    const normalizedStabilityDelay = Math.max(0, Number(captureStabilityDelayMs) || 0);
    if (normalizedStabilityDelay > 0) {
      const previousStability = captureStabilityByNode.get(node);
      if (
        !previousStability
        || previousStability.fingerprint !== fingerprint
      ) {
        captureStabilityByNode.set(node, {
          fingerprint,
          stableAt: now() + normalizedStabilityDelay,
        });
        scheduleScan(normalizedStabilityDelay);
        return null;
      }
      if (previousStability.stableAt > now()) {
        scheduleScan(previousStability.stableAt - now());
        return null;
      }
    }

    const previousFailure = captureFailureByNode.get(node);
    if (
      previousFailure?.fingerprint === fingerprint
      && previousFailure.retryAt > now()
    ) {
      scheduleScan(previousFailure.retryAt - now());
      return null;
    }

    const previousAttempt = hasStableOccurrence
      ? captureAttemptByFingerprint.get(fingerprint)
      : captureAttemptByNode.get(node);
    const captureAttempt = previousAttempt?.fingerprint === fingerprint
      ? previousAttempt
      : {
        alertText,
        captureContext,
        clientCaptureId: createDispatchAlertCaptureId(randomUuid),
        fingerprint,
        image: null,
        occurredAt: new Date(now()).toISOString(),
      };
    captureAttemptByNode.set(node, captureAttempt);
    if (hasStableOccurrence) {
      captureAttemptByFingerprint.set(fingerprint, captureAttempt);
    }
    pendingNodes.add(node);

    try {
      await waitForPaint();
      if (
        !isCaptureStillAllowed(policy, policy.campaignId)
        || !isVisibleCaptureNode(node)
        || currentRoute.pathname !== routeAtOccurrence.pathname
        || currentRoute.search !== routeAtOccurrence.search
      ) {
        return null;
      }

      const currentSnapshot = readCaptureSnapshot(node, policy, routeAtOccurrence);
      if (
        !currentSnapshot
        || currentSnapshot.alertType !== alertType
        || currentSnapshot.fingerprint !== fingerprint
      ) {
        return null;
      }

      let image = captureAttempt.image;
      if (!image) {
        const preferredPixelRatio = Math.min(
          Math.max(Number(windowObject?.devicePixelRatio) || 1, 1),
          2,
        );
        image = await renderNode(node, { pixelRatio: preferredPixelRatio });
        if (Number(image?.size) > policy.maxUploadBytes && preferredPixelRatio > 1) {
          image = await renderNode(node, { pixelRatio: 1 });
        }
      }
      if (!image || image.type !== "image/png") {
        throw new Error("La capture produite n'est pas un PNG valide.");
      }
      if (!Number.isFinite(image.size) || image.size <= 0 || image.size > policy.maxUploadBytes) {
        throw new Error("La capture PNG dépasse la taille autorisée.");
      }
      const renderedSnapshot = readCaptureSnapshot(node, policy, routeAtOccurrence);
      if (
        !isCaptureStillAllowed(policy, policy.campaignId)
        || !isVisibleCaptureNode(node)
        || currentRoute.pathname !== routeAtOccurrence.pathname
        || currentRoute.search !== routeAtOccurrence.search
        || !renderedSnapshot
        || renderedSnapshot.alertType !== alertType
        || renderedSnapshot.fingerprint !== fingerprint
      ) {
        return null;
      }
      captureAttempt.alertText = renderedSnapshot.alertText;
      captureAttempt.image = image;

      const uploadAbortController = typeof AbortControllerImpl === "function"
        ? new AbortControllerImpl()
        : null;
      if (uploadAbortController) {
        activeUploadAbortControllers.add(uploadAbortController);
      }

      try {
        const response = await uploadCapture({
          alertText: captureAttempt.alertText,
          alertType,
          campaignId: policy.campaignId,
          captureContext: captureAttempt.captureContext,
          clientCaptureId: captureAttempt.clientCaptureId,
          dispatchDate: routeAtOccurrence.dispatchDate,
          dispatchPeriod: routeAtOccurrence.dispatchPeriod,
          image,
          occurredAt: captureAttempt.occurredAt,
          signal: uploadAbortController?.signal,
          sourcePath: routeAtOccurrence.pathname,
        });
        capturedFingerprintByNode.set(node, fingerprint);
        if (hasStableOccurrence) {
          capturedOccurrenceFingerprints.add(fingerprint);
          captureAttemptByFingerprint.delete(fingerprint);
        }
        if (renderedSnapshot.incidentKey) {
          capturedIncidentByKey.set(renderedSnapshot.incidentKey, {
            alertType,
            capturedAt: now(),
          });
        }
        for (const equivalentSnapshot of equivalentSnapshots) {
          rememberCapturedAlternative(equivalentSnapshot, policy);
        }
        captureAttemptByNode.delete(node);
        captureFailureByNode.delete(node);
        return response;
      } finally {
        if (uploadAbortController) {
          activeUploadAbortControllers.delete(uploadAbortController);
        }
      }
    } catch (error) {
      if (!disposed && error?.name !== "AbortError") {
        captureFailureByNode.set(node, {
          fingerprint,
          retryAt: now() + CAPTURE_FAILURE_RETRY_DELAY_MS,
        });
        if (error?.code === CAMPAIGN_FULL_ERROR_CODE) {
          blockedCampaignId = policy.campaignId;
          blockedCampaignMessage = error.message
            || "La campagne de captures a atteint sa limite.";
          stopCaptureActivity("error", blockedCampaignMessage);
        } else if (POLICY_INVALIDATING_UPLOAD_STATUSES.has(Number(error?.status))) {
          cachedPolicy = null;
          currentEtag = "";
          policyVerifiedAt = null;
          stopCaptureActivity("error", "La politique de capture doit être vérifiée de nouveau.");
          void pollPolicy();
        }
      }
      return null;
    } finally {
      pendingNodes.delete(node);
      if (!disposed && state.isActive) {
        scheduleScan();
      }
    }
  }

  async function scanNow() {
    if (
      disposed
      || !state.isActive
      || !cachedPolicy
      || !isCaptureStillAllowed(cachedPolicy, cachedPolicy.campaignId)
      || typeof documentObject?.querySelectorAll !== "function"
    ) {
      return [];
    }

    const nodes = [...documentObject.querySelectorAll(DISPATCH_ALERT_CAPTURE_SELECTOR)];
    const candidates = selectCaptureCandidates(nodes, cachedPolicy);
    return Promise.all(candidates.map((candidate) => captureNode(candidate, cachedPolicy)));
  }

  function scheduleScan(delayMs = 0) {
    if (typeof setTimeoutImpl !== "function") {
      return;
    }
    const normalizedDelay = Math.max(0, Number(delayMs) || 0);
    const dueAt = now() + normalizedDelay;
    if (scanTimeoutId != null && scanDueAt != null && scanDueAt <= dueAt) {
      return;
    }
    if (scanTimeoutId != null && typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(scanTimeoutId);
    }
    scanDueAt = dueAt;
    scanTimeoutId = setTimeoutImpl(() => {
      scanDueAt = null;
      scanTimeoutId = null;
      void scanNow();
    }, normalizedDelay);
  }

  function ensureObserver() {
    if (
      mutationObserver
      || typeof MutationObserverImpl !== "function"
      || !documentObject?.body
    ) {
      scheduleScan();
      return;
    }
    mutationObserver = new MutationObserverImpl(scheduleScan);
    mutationObserver.observe(documentObject.body, {
      attributeFilter: [
        "data-dispatch-alert-capture-context",
        "data-dispatch-alert-capture-occurrence",
        "data-dispatch-alert-capture-type",
      ],
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    });
    scheduleScan();
  }

  function armPolicyFreshnessTimer(policy) {
    clearPolicyFreshnessTimer();
    if (
      disposed
      || cachedPolicy !== policy
      || !isPolicyCurrentlyEnabled(policy, now())
      || !isDocumentVisible()
      || !canRunOnCurrentRoute()
      || blockedCampaignId === policy.campaignId
      || !Number.isFinite(policyVerifiedAt)
      || typeof setTimeoutImpl !== "function"
    ) {
      return;
    }

    const verifiedAt = policyVerifiedAt;
    const delay = Math.max(
      0,
      verifiedAt + resolvePolicyFreshnessWindow(policy) - now(),
    );
    policyFreshnessTimeoutId = setTimeoutImpl(() => {
      policyFreshnessTimeoutId = null;
      if (
        disposed
        || cachedPolicy !== policy
        || policyVerifiedAt !== verifiedAt
      ) {
        return;
      }
      if (isPolicyFresh(policy, now())) {
        armPolicyFreshnessTimer(policy);
        return;
      }
      stopCaptureActivity(
        "error",
        "La politique de capture n'a pas pu être vérifiée à temps.",
      );
      void pollPolicy();
    }, delay);
  }

  function applyPolicy(policy) {
    cachedPolicy = policy;
    if (blockedCampaignId && blockedCampaignId !== policy.campaignId) {
      blockedCampaignId = "";
      blockedCampaignMessage = "";
    }
    const isEnabled = isPolicyCurrentlyEnabled(policy, now());
    if (!isEnabled || !isDocumentVisible() || !canRunOnCurrentRoute()) {
      stopCaptureActivity(isEnabled ? "paused" : "inactive");
      return;
    }
    if (blockedCampaignId === policy.campaignId) {
      stopCaptureActivity(
        "error",
        blockedCampaignMessage || "La campagne de captures a atteint sa limite.",
      );
      return;
    }

    publishState({
      campaignId: policy.campaignId,
      errorMessage: "",
      isActive: true,
      revision: policy.revision,
      status: "active",
    });
    ensureObserver();
    armPolicyFreshnessTimer(policy);
  }

  function resolvePollDelay() {
    const configuredDelay = Math.max(
      cachedPolicy?.pollIntervalMs || DISPATCH_ALERT_CAPTURE_POLL_INTERVAL_MS,
      minimumPollIntervalMs,
    );
    const expiresAt = parseTimestamp(cachedPolicy?.expiresAt);
    if (expiresAt == null) {
      return configuredDelay;
    }
    return Math.max(Math.min(configuredDelay, expiresAt - now()), minimumPollIntervalMs);
  }

  function schedulePolicyPoll() {
    clearPollTimer();
    if (
      disposed
      || !started
      || !isDocumentVisible()
      || !canRunOnCurrentRoute()
      || typeof setTimeoutImpl !== "function"
    ) {
      return;
    }
    pollTimeoutId = setTimeoutImpl(() => {
      pollTimeoutId = null;
      void pollPolicy();
    }, resolvePollDelay());
  }

  async function pollPolicy() {
    if (
      disposed
      || !started
      || !isDocumentVisible()
      || !canRunOnCurrentRoute()
    ) {
      return null;
    }
    if (activePollPromise) {
      return activePollPromise;
    }

    policyAbortController = typeof AbortControllerImpl === "function"
      ? new AbortControllerImpl()
      : null;
    if (
      policyAbortController
      && typeof setTimeoutImpl === "function"
      && Number.isFinite(policyRequestTimeoutMs)
      && policyRequestTimeoutMs > 0
    ) {
      policyRequestTimeoutId = setTimeoutImpl(() => {
        policyRequestTimeoutId = null;
        policyAbortController?.abort?.();
      }, policyRequestTimeoutMs);
    }
    const requestedEtag = cachedPolicy ? currentEtag : "";
    activePollPromise = Promise.resolve(fetchPolicy({
      etag: requestedEtag,
      signal: policyAbortController?.signal,
    }))
      .then((result) => {
        if (disposed) {
          return null;
        }
        if (result?.notModified) {
          if (!cachedPolicy) {
            throw new Error("La politique de capture en cache est indisponible.");
          }
          currentEtag = result.etag || currentEtag;
          policyVerifiedAt = now();
          applyPolicy(cachedPolicy);
          return cachedPolicy;
        }

        const nextPolicy = normalizeDispatchAlertCapturePolicy(result?.policy);
        currentEtag = typeof result?.etag === "string" ? result.etag : "";
        policyVerifiedAt = now();
        applyPolicy(nextPolicy);
        return nextPolicy;
      })
      .catch((error) => {
        if (!disposed) {
          cachedPolicy = null;
          currentEtag = "";
          policyVerifiedAt = null;
          stopCaptureActivity(
            "error",
            "La politique de capture n'a pas pu être vérifiée.",
          );
        }
        return null;
      })
      .finally(() => {
        if (policyRequestTimeoutId != null && typeof clearTimeoutImpl === "function") {
          clearTimeoutImpl(policyRequestTimeoutId);
        }
        policyRequestTimeoutId = null;
        activePollPromise = null;
        policyAbortController = null;
        schedulePolicyPoll();
      });

    return activePollPromise;
  }

  function handleVisibilityChange() {
    clearPollTimer();
    if (!isDocumentVisible()) {
      stopCaptureActivity("paused");
      return Promise.resolve(null);
    }
    if (!canRunOnCurrentRoute()) {
      stopCaptureActivity("paused");
      return Promise.resolve(null);
    }
    return pollPolicy();
  }

  function start() {
    if (started || disposed) {
      return activePollPromise || Promise.resolve(cachedPolicy);
    }
    started = true;
    documentObject?.addEventListener?.("visibilitychange", handleVisibilityChange);
    return handleVisibilityChange();
  }

  return {
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      started = false;
      clearPollTimer();
      clearPolicyFreshnessTimer();
      disconnectObserver();
      if (policyRequestTimeoutId != null && typeof clearTimeoutImpl === "function") {
        clearTimeoutImpl(policyRequestTimeoutId);
      }
      policyRequestTimeoutId = null;
      documentObject?.removeEventListener?.("visibilitychange", handleVisibilityChange);
      policyAbortController?.abort?.();
      for (const controller of activeUploadAbortControllers) {
        controller.abort?.();
      }
      activeUploadAbortControllers.clear();
      publishState({
        campaignId: "",
        errorMessage: "",
        isActive: false,
        revision: 0,
        status: "idle",
      });
    },
    getSnapshot() {
      return { ...state };
    },
    refreshPolicy: pollPolicy,
    refreshRoute(nextRoute = {}) {
      const previousPathname = currentRoute.pathname;
      const previousSearch = currentRoute.search;
      currentRoute = readRouteContext(nextRoute);
      if (
        currentRoute.pathname === previousPathname
        && currentRoute.search === previousSearch
      ) {
        return activePollPromise || Promise.resolve(cachedPolicy);
      }

      clearPollTimer();
      if (!canRunOnCurrentRoute()) {
        stopCaptureActivity("paused");
        return Promise.resolve(null);
      }
      return handleVisibilityChange();
    },
    scanNow,
    start,
  };
}
